import * as React from 'react'

import { z } from 'zod'
export const schema = z.object({
  id: z.number(),
  title: z.string(),
  proposalId: z.number(),
  quotationId: z.number(),
  offerId: z.number(),
  dueDate: z.string(),
  status: z.string(),
  editDate: z.string(),
  permissions: z.array(
    z.object({
      id: z.number(),
      user: z.object({
        id: z.number(),
        firstname: z.string(),
        lastname: z.string(),
        email: z.string(),
      }),
    }),
  ),
})
import api from '@/lib/api'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
  useDisclosure,
} from '@nextui-org/react'
import { Icon } from '@iconify/react'
import { PowerDocsEventsName } from '@/types/SocketEvents'
import { useSocketContextData } from '@/context/socket'
import dayjs from 'dayjs'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { format } from 'date-fns'
import OverView from './components/Overview'
import DocumentList from './components/DocumentList'
import AttachmentList from './components/AttachmentList'
import toast from 'react-hot-toast'
import { useWorkspaceContextData } from 'src/context/workspace'
interface MemberUser {
  id: number
  firstname: string
  lastname: string
  email: string
}
interface Member {
  id: number
  role: 'OWNER' | 'EDITOR' | 'VIEWER'
  user: MemberUser
}
const inActiveButtonClass = 'bg-transparent text-default-400 pl-5 pr-5'
const activeButtonClass = 'text-black-500 font-medium bg-white rounded-md  pl-5 pr-5'

export default function PowerDocProjectOverView({ tabSlug = 'documents' }: { tabSlug?: string }) {
  const [doc, setDoc] = useState<any>({})
  const [initialized, setInitialized] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<string>(tabSlug.charAt(0).toUpperCase() + tabSlug.slice(1))
  const { documentUUID } = useParams()
  const [requestChangeText, setRequestChangeText] = useState<string>('')
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false)
  const [dueDate, setDueDate] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const loadDocument = async () => {
    const res = await api.get(`/powerdocs/${documentUUID}`)
    setDoc(res.data)
    setDueDate(res.data.dueDate)
    setInitialized(true)
  }
  const navigate = useNavigate()
  const { io } = useSocketContextData()
  const params = useParams()
  const {
    isOpen: isReviewModalOpen,
    onClose: onReviewModalClose,
    onOpenChange: onReviewModalOpenChange,
  } = useDisclosure()
  const {
    isOpen: isRejectModalOpen,
    onClose: onRejectModalClose,
    onOpenChange: onRejectModalOpenChange,
  } = useDisclosure()

  useEffect(() => {
    loadDocument()
  }, [])
  const updateDueDate = (value: Date) => {
    setDueDate(value as unknown as string)
    setCalendarOpen(false)
    setDoc((prev: any) => ({
      ...prev,
      dueDate,
    }))
    io.emit(PowerDocsEventsName.UPDATE_POWERDOC_DUE_DATE, {
      dueDate: value,
      documentUUID: doc.uuid,
      token: localStorage.getItem('accessToken'),
      variant: 'project',
    })
  }
  const { profile } = useWorkspaceContextData()
  const [loading, setLoading] = useState<boolean>(false)
  const sendRejectRequest = async () => {
    setLoading(true)
    await toast.promise(
      io.emitWithAck(PowerDocsEventsName.REJECT_TENDER, {
        documentUUID: doc.uuid,
        requestChangeText,
        token: localStorage.getItem('accessToken'),
      }),
      {
        loading: 'Sending request...',
        success: () => {
          setLoading(false)
          onRejectModalClose()
          setRequestChangeText('')
          return 'Request sent successfully!'
        },
        error: (err) => {
          setLoading(false)
          return `Error sending request: ${err.message}`
        },
      },
    )
  }
  const sendRequestChange = async () => {
    setLoading(true)
    await toast.promise(
      io.emitWithAck(PowerDocsEventsName.REQUEST_CHANGES, {
        documentUUID: doc.uuid,
        requestChangeText,
        token: localStorage.getItem('accessToken'),
      }),
      {
        loading: 'Sending request...',
        success: () => {
          setLoading(false)
          onReviewModalClose()
          setRequestChangeText('')
          return 'Request sent successfully!'
        },
        error: (err) => {
          setLoading(false)
          return `Error sending request: ${err.message}`
        },
      },
    )
  }
  return (
    <div className="flex flex-col w-full max-w-screen-xl h-screen p-3">
      <Breadcrumbs className={'pl-2 pt-4'}>
        <BreadcrumbItem onClick={() => navigate('/pipeline')}>
          <Icon icon={'lucide:home'} /> Project pipeline
        </BreadcrumbItem>
        <BreadcrumbItem onClick={() => navigate(`/client/${params.clientUUID}/project/${documentUUID}/documents`)}>
          Project
        </BreadcrumbItem>
      </Breadcrumbs>
      <div className="flex w-full pt-8 pb-6 px-2 2xl:px-8">
        <header className="flex flex-col w-full gap-4">
          <div className="w-fit flex flex-row items-center gap-2">
            <div className={'w-fit flex items-center gap-2 min-h-[36px]'}>
              <Icon
                icon={'lucide:square-chevron-left'}
                width={25}
                color={'#71717a'}
                className="cursor-pointer"
                onClick={() => navigate(-1)}
              />
              <h1 className="text-3xl font-bold text-default-900 capitalize">Project ▸ {doc?.title}</h1>
            </div>
          </div>
          <div className={'flex flex-row gap-4'}>
            <div className={'border-1 border-default rounded-md pt-1 pb-1 pl-4 pr-4 flex flex-row gap-2'}>
              <Icon icon={'lucide:handshake'} width={16} color={'#71717a'} />
              <span className={'text-xs'}>Client: {doc.client?.name ?? 'No Client Selected'}</span>
            </div>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <div
                  className={
                    'border-1 border-default rounded-md pt-1 pb-1 pl-4 pr-4 flex flex-row gap-2 cursor-pointer'
                  }
                  onClick={() => setCalendarOpen(true)}
                >
                  <Icon icon={'lucide:calendar-days'} width={16} color={'#71717a'} />
                  <span className={'text-xs'}>
                    {dueDate ? `${format(dueDate, 'PPP')} | ${dayjs(dueDate).fromNow()}.` : 'No due date specified yet'}
                  </span>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  initialFocus
                  mode="single"
                  selected={dueDate as unknown as Date}
                  //@ts-ignore
                  onSelect={updateDueDate}
                />
              </PopoverContent>
            </Popover>
          </div>
        </header>
      </div>
      <div className="flex flex-1 w-full pb-2 max-w-full max-h-full overflow-y-auto flex-col px-2 2xl:px-8">
        <div className="flex flex-row space-between items-center">
          <div className={'p-1 rounded-xl w-fit bg-default-100'}>
            {['Documents', 'Overview', 'Attachments'].map((item) => (
              <Button
                key={item}
                isDisabled={isGenerating}
                size={'sm'}
                className={activeTab.toLowerCase() === item.toLowerCase() ? activeButtonClass : inActiveButtonClass}
                onPress={() => {
                  setActiveTab(item)
                  navigate(`/client/${params.clientUUID}/project/${params.documentUUID}/${item.toLowerCase()}`)
                }}
              >
                {item}
              </Button>
            ))}
          </div>
          <div className="flex flex-1 justify-end items-center gap-2">
            {doc.MyPublicTenders?.filter(
              (item: any) => (item.phase === 'PROCESSING' || item.phase === 'NEEDS_REVIEW') && !item.rejected,
            )?.length === 0 && profile.serviceTier !== 'SELF_SERVE' && (
              <>
                <Button
                  isDisabled={isGenerating}
                  size={'md'}
                  variant="solid"
                  color="danger"
                  onPress={() => {
                    onRejectModalOpenChange()
                  }}
                >
                  <Icon icon="lucide:send" width="18" className="text-white" />
                  Reject Tender
                </Button>
                <Button
                  isDisabled={isGenerating}
                  size={'md'}
                  variant="solid"
                  color="secondary"
                  onPress={() => {
                    onReviewModalOpenChange()
                  }}
                >
                  <Icon icon="lucide:pen" width="18" className="text-white" />
                  Request Changes
                </Button>
              </>
            )}
          </div>
        </div>

        <Modal size={'2xl'} isOpen={isReviewModalOpen} onClose={onReviewModalClose}>
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader className="flex flex-col gap-1">Tell us more</ModalHeader>
                <ModalBody>
                  <p className={'text-xl'}>
                    What are the changes you would like to request? Please be as specific as possible so that our AI
                    provide you with the best possible response.
                  </p>
                  <Textarea
                    className={'mt-2'}
                    placeholder="Please describe the changes you would like to request..."
                    minRows={4}
                    maxRows={6}
                    onChange={(e) => {
                      setRequestChangeText(e.target.value)
                    }}
                  />
                </ModalBody>
                <ModalFooter>
                  <Button
                    color="danger"
                    variant={'solid'}
                    onPress={async () => {
                      onClose()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button color="success" variant={'bordered'} onPress={sendRequestChange}>
                    🚀Send Request
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>

        <Modal size={'2xl'} isOpen={isRejectModalOpen} onClose={onRejectModalClose}>
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader className="flex flex-col gap-1">Tell us more</ModalHeader>
                <ModalBody>
                  <p className={'text-xl'}>
                    Would you like to tell us more about the reason why this proposal didn&apos;t meet your
                    expectations. This will help us to enhance our AI models to provide you with a better result next
                    time.
                  </p>
                  <Textarea
                    className={'mt-2'}
                    placeholder="Please try to be specific to help us improve our AI Model..."
                    minRows={4}
                    maxRows={6}
                    onChange={(e) => {
                      setRequestChangeText(e.target.value)
                    }}
                  />
                </ModalBody>
                <ModalFooter>
                  <Button
                    color="danger"
                    variant={'solid'}
                    onPress={async () => {
                      onClose()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button color="success" variant={'bordered'} onPress={sendRejectRequest}>
                    Reject Proposal
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
        {activeTab === 'Documents' && (
          <DocumentList
            doc={doc}
            setDoc={setDoc}
            isProcessing={
              doc.MyPublicTenders?.filter(
                (item: any) => (item.phase === 'PROCESSING' || item.phase === 'NEEDS_REVIEW') && !item.rejected,
              )?.length > 0 && profile.serviceTier !== 'SELF_SERVE'
            }
          />
        )}
        {activeTab === 'Overview' && <OverView doc={doc} setDoc={setDoc} />}
        {activeTab === 'Attachments' && <AttachmentList doc={doc} setDoc={setDoc} />}
      </div>
    </div>
  )
}
